/**
 * One sentence per clone failure, shared by every surface that can start a
 * clone: the /community gallery, the homepage "Clone this" and template cards,
 * the /templates gallery and detail page, and the editor's "Duplicate project".
 *
 * Before this, all five collapsed 404 and 500 alike into a single "Couldn't
 * clone this. Try again." — so a deleted source, a source the caller can't see,
 * and an outright server error were indistinguishable, and a user told "it says
 * I don't have access" could not be pointed at anything. Worse, "try again" is
 * exactly wrong advice for a permanently-gone source and exactly right advice
 * for a transient lookup failure, and the same string was giving both.
 *
 * The split that matters to the user is RETRYABLE vs PERMANENT — see
 * {@link isRetryableCloneError}. The split that matters to the operator lives in
 * the server logs (`[fork] DENY reason=…`), never here: the routes deliberately
 * fuse "missing" and "private" into one code so a distinct response can't
 * confirm a project's existence to someone probing UUIDs.
 */

/**
 * Codes both clone routes emit, mapped to the copy shown when the server
 * doesn't send its own `message`. The server's sentence always wins — this is
 * the floor, not the ceiling.
 */
export const CLONE_ERROR_COPY: Record<string, string> = {
  // Missing, private-and-not-yours, or a malformed id. Deliberately one
  // sentence for all three: it has to be true without saying which.
  source_unavailable:
    "This project isn't available to clone. It may have been deleted, or its owner made it private.",
  template_unavailable: "This template isn't available right now.",
  // The lookup itself failed. Our fault, and worth retrying — this is the case
  // that used to show up as "not found" and read as "you don't have access".
  source_lookup_failed:
    "We couldn't check this project just now. Try again in a moment.",
  template_lookup_failed:
    "We couldn't look up this template just now. Try again in a moment.",
  source_files_unreadable:
    "We couldn't read this project's files just now. Try again in a moment.",
  server_error: "Something went wrong on our side. Try again in a moment.",
};

/**
 * True when the failure is ours and transient, so the surface should offer a
 * Retry rather than send the user away. A permanently-unavailable source must
 * NOT get a retry button — inviting someone to re-click a clone that can never
 * succeed is how the old single-string toast wasted people's time.
 */
export function isRetryableCloneError(
  code: string | undefined,
  status?: number,
): boolean {
  if (typeof status === "number" && status >= 500) return true;
  return (
    code === "source_lookup_failed" ||
    code === "template_lookup_failed" ||
    code === "source_files_unreadable" ||
    code === "server_error"
  );
}

/**
 * Resolve the message to show. Precedence: the server's own sentence (only it
 * knows which of several cases a shared code covers), then this file's copy for
 * a known code, then the caller's surface-specific fallback.
 *
 * `serverMessage` must be the response body's `message`, never its `error` —
 * `error` is a machine code and showing "source_unavailable" to a user is the
 * bug this helper exists to prevent.
 */
export function cloneErrorMessage(
  code: string | undefined,
  serverMessage: string | undefined,
  fallback: string,
): string {
  if (typeof serverMessage === "string" && serverMessage.trim()) {
    return serverMessage.trim();
  }
  return (code && CLONE_ERROR_COPY[code]) || fallback;
}
